<template>
  <div class="card-slider">
    <div class="card-list" :style="{ transform: `translateX(-${currentSlide * slideWidth}px)` }"
      @touchstart="handleTouchStart" @touchmove="handleTouchMove" @touchend="handleTouchEnd">
      <div v-for="(card, index) in cards" :key="index" class="card">
        <img :src="card.image" :alt="`${card.title} - Foto`" />
        <div class="card-info">
          <p class="title">{{ card.title }}</p>
          <p class="description">{{ card.description }}</p>
          <p v-if="card.price" class="price">{{ card.price }}</p>
        </div>
        <button class="read-more" @click="goTo(card)">
          Saiba Mais <span class="arrow">➤</span>
        </button>
      </div>
    </div>

    <div class="dots">
      <span v-for="(dot, index) in (totalSlides + 1)" :key="index" :class="['dot', { active: currentSlide === index }]"
        @click="goToSlide(index)"></span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router';

const router = useRouter();

const cards = ref([
  {
    image: require('@/assets/abismo_anhumas.jpg'),
    title: 'ABISMO ANHUMAS',
    description: 'MERGULHE EM UMA AVENTURA SURREAL',
    //price: 'R$500'
    link: 'grutas',
    params: { id: 'abismo anhumas' }
  },
  {
    image: require('@/assets/gruta_azul.jpg'),
    title: 'GRUTA DO LAGO AZUL',
    description: 'CONHEÇA O CARTÃO POSTAL DE BONITO-MS',
    //price: 'R$750'
    link: 'grutas',
    params: { id: 'gruta do lago azul' }
  },
  {
    image: require('@/assets/lagoa_misteriosa.jpg'),
    title: 'LAGOA MISTERIOSA',
    description: 'UM DOS MAIS LINDOS PASSEIOS',
    //price: 'R$650'
    link: 'flutuacoes',
    params: { id: 'lagoa misteriosa' }
  }
])

const currentSlide = ref(0)
const screenWidth = ref(window.innerWidth)
const autoPlayInterval = ref(null)

// Touch variables
const touchStartX = ref(0)
const touchEndX = ref(0)
const isDragging = ref(false)

const slideWidth = computed(() => {
  return screenWidth.value <= 540 ? 176 : 197
})

const totalSlides = computed(() => {
  if (screenWidth.value <= 540) {
    return cards.value.length - 1
  }
  return cards.value.length - 3
})

const handleResize = () => {
  screenWidth.value = window.innerWidth
  currentSlide.value = 0
}

const startAutoPlay = () => {
  autoPlayInterval.value = setInterval(() => {
    if (currentSlide.value >= totalSlides.value) {
      currentSlide.value = 0
    } else {
      currentSlide.value++
    }
  }, 10000)
}

const stopAutoPlay = () => {
  if (autoPlayInterval.value) {
    clearInterval(autoPlayInterval.value)
    autoPlayInterval.value = null
  }
}

// Touch handlers
const handleTouchStart = (e) => {
  touchStartX.value = e.touches[0].clientX
  touchEndX.value = e.touches[0].clientX
  isDragging.value = false
  stopAutoPlay()
}

const handleTouchMove = (e) => {
  touchEndX.value = e.touches[0].clientX
  const diff = Math.abs(touchStartX.value - touchEndX.value)

  if (diff > 10) {
    isDragging.value = true
    e.preventDefault()
  }
}

const handleTouchEnd = () => {
  if (!isDragging.value) {
    startAutoPlay()
    return
  }

  const swipeThreshold = 50
  const diff = touchStartX.value - touchEndX.value

  if (Math.abs(diff) > swipeThreshold) {
    if (diff > 0 && currentSlide.value < totalSlides.value) {
      // Swipe left - next slide
      currentSlide.value++
    } else if (diff < 0 && currentSlide.value > 0) {
      // Swipe right - previous slide
      currentSlide.value--
    }
  }

  isDragging.value = false
  startAutoPlay()
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
  startAutoPlay()
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  stopAutoPlay()
})

const goToSlide = (index) => {
  currentSlide.value = index
}

const goTo = (card) => {
  router.push({ name: card.link, query: card.params })
}
</script>

<style lang="scss" scoped>
.card-slider {
  overflow: hidden;
  width: 573px;
  position: relative;
  /* touch-action: pan-y pinch-zoom; */
}

.card-list {
  display: flex;
  gap: 16px;
  transition: transform 0.3s ease;
  width: fit-content;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.card {
  width: 160px;
  height: 215px;
  background: $branco;
  border-radius: 16px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0 2px 4px rgba($preto, .1);

  &:hover {
    box-shadow: 0 4px 8px rgba($preto, .2);
    //transform: translateY(-4px);
    transition: box-shadow 0.3s ease, transform 0.3s ease;
  }

  img {
    width: 100%;
    height: 90px;
    object-fit: cover;
    border-radius: 8px;
  }

  .card-info {
    margin-top: 8px;

    .title {
      font-size: 12px;
      font-weight: bold;
      white-space: pre-line;
      margin: 0;
    }

    .description {
      font-size: 11px;
      color: $cinza;
      margin: 4px 0;
    }

    .price {
      font-size: 14px;
      color: $cinza;
      margin-top: 8px;
    }
  }

  .read-more {
    background-color: $verdeClaro;
    color: $preto;
    border: none;
    padding: 6px 10px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    margin-top: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    transition: all 0.25s ease;

    .arrow {
      font-size: 14px;
      color: $branco;
    }

    &:hover {
      color: $branco;
      background-color: $laranjaForte;
    }
  }
}

.dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.5);
    cursor: pointer;
    transition: background 0.3s ease;

    &.active {
      background: $branco;
    }
  }
}

@media (max-width: 540px) {
  .card-slider {
    width: 370px;
    margin: 0 auto;
    /* touch-action: pan-x; */

    .card {
      padding: 5px;
      width: 170px;
    }
  }
}
</style>
